import React, {useCallback, useEffect, useRef, useState} from "react";
import { Box, Button, IconButton, TextField } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import InsertMenu from "./InsertMenu";
import { IInsertOption } from "./insertOptions";
import { StateTools } from "./StateTools";
import { connect } from "react-redux";
import { updateFocus, updateMode } from "../../redux/slices/editor";
import { Spellcheck } from "@mui/icons-material";
import { httpsCallable, getFunctions } from "firebase/functions";
import { IScript } from "./Writer";
import { IValues } from "./ScriptRender";
import {selectUserId} from "../../redux/selectors/editor";
import ScriptVersion from "./ScriptVersion";

const useStyles = makeStyles(theme => ({
    state: {
        fontFamily: 'courier',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(12),
        marginRight: theme.spacing(12),
        zIndex: 1
    },
    textTools: {
        zIndex: "inherit"
    },
    stateKey: {
        color: "#C0C0C0",
        cursor: "pointer",
        zIndex: "inherit"
    },
    textField: {
        backgroundColor: "#E0E0E0",
        padding: theme.spacing(1),
        borderRadius: 4,
        "& .MuiInputBase-multiline": {
            fontFamily: 'courier',
            padding: 0,
        },
        "& .MuiInputBase-inputMultiline": {
            lineHeight: 1.5,
        },
        '& .MuiInputBase-inputMultiline::-webkit-scrollbar': {
            width: '0.4em'
        },
        '& .MuiInputBase-inputMultiline::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        },
    },
    tools: {
        zIndex: 1
    }
}));

export interface IGrammarRequest
{
    project: string
    state: string
    text: string
}
export interface IGrammarResult
{
    matches: IGrammarRule[]
}
export interface IGrammarRule
{
    context: {
        length: number
        offset: number
        text: string
    },
    length: number,
    offset: number,
    message: string,
    replacements: IValues[],
    rule: {
        description: string,
        issueType: string,
        urls: string[]
    },
    sentence: string,
    shortMessage: string
}
export interface IEditSelection
{
    script: string
    overrideSelection: boolean
    selectionStart?: number
    selectionEnd?: number
    selectionDirection?: "forward" | "backward" | "none"
}
interface IProps
{
    index: number
    onEdit: (state: string | undefined) => void
    onUpdateGrammar: (state: string, text: string, grammar: IGrammarRule[]) => void
    project: string
    setSelectionRange: (state: string, start: number, end: number) => void
    script: IScript
    state: string
    stateURL: string
    updateScript: (index: number, state: string, script: string) => void
    updateMode: (mode: string) => void,
    updateFocus: (focus: string) => void,
    userKey: string,
}

function EditState({ stateURL, index, onEdit, setSelectionRange, updateMode, updateFocus,
    onUpdateGrammar, project, script, state, updateScript, userKey}: IProps)
{
    const classes = useStyles();
    const [edited, setEdited] = useState<IEditSelection>({ script: script.text, overrideSelection: false });
    const [position, setPosition] = useState<{ top: number, left: number }>();
    const [initialFocus, setInitialFocus] = useState(false);
    const updateGrammarRef = useRef(httpsCallable<IGrammarRequest, IGrammarResult>(
        getFunctions(undefined, "europe-west2"), "updateGrammar"));
    const inputRef = useRef<HTMLTextAreaElement>();
    const handleScriptChange = ({ target }: React.ChangeEvent<HTMLInputElement>) =>
    {
        setEdited({ script: target.value, overrideSelection: edited.overrideSelection });
    };
    const handleMouseUp = () =>
    {
        if (inputRef.current)
            setSelectionRange(state, inputRef.current.selectionStart, inputRef.current.selectionEnd);
    }
    const handleBlur = () => {
        save();
    };
    const handleMenuClose = () =>
    {
        setPosition(undefined);
    };
    const handleInsert = useCallback((option: IInsertOption) =>
    {
        setEdited(option.insert(edited));
    }, [edited, setEdited]);
    
    const handleSpellcheckClick = () =>
    {
        updateScript(index, state, edited.script);
        updateGrammar();
        onEdit(undefined);
    }
    const handleSelectVersion = (script: string) => {
        setEdited({ script, overrideSelection: edited.overrideSelection });
    }
    const handleCancelClick = () => {
        cancel();
    };
    const handleSaveClick = () => {
        save();
    };
    const updateGrammar = useCallback(() =>
    {
        updateGrammarRef.current({
            project,
            state,
            text: edited.script
        }).then((result) =>
        {
            onUpdateGrammar(state, edited.script, result.data.matches);
        }).catch(console.error);
    }, [onUpdateGrammar, project, state, edited.script]);
    const cancel = useCallback(() => {
        onEdit(undefined);
    }, [onEdit]);
    const save = useCallback(() =>
    {
        if (edited.script !== script.text && position === undefined)
        {
            updateScript(index, state, edited.script);
            updateGrammar();
        } else {
            onEdit(undefined);
        }
    }, [edited.script, index, onEdit, position, script.text, state, updateGrammar, updateScript]);

    // Focus on creation to force spell checking
    useEffect(() =>
    {
        if (inputRef.current && !initialFocus)
        {
            inputRef.current.focus();
            setInitialFocus(true)
        }
    }, [initialFocus, setInitialFocus]);
    useEffect(() =>
    {
        if (inputRef.current && edited.overrideSelection)
        {
            setEdited({ ...edited, overrideSelection: false });
            inputRef.current.setSelectionRange(edited.selectionStart || 0, edited.selectionEnd || 0,
                edited.selectionDirection || "none");
        }
    }, [edited, edited.overrideSelection]);
    useEffect(() =>
    {
        const createCopy = (textArea: HTMLTextAreaElement): HTMLDivElement =>
        {
            const copy = document.createElement('div');
            const style = getComputedStyle(textArea);

            copy.textContent = textArea.value;
            ['fontFamily', 'fontSize', 'fontWeight', 'wordWrap', 'whiteSpace', 'borderLeftWidth', 'borderTopWidth',
                'borderRightWidth', 'borderBottomWidth'].forEach(function (key)
                {
                    // @ts-ignore
                    copy.style[key] = style[key];
                });
            copy.style.overflow = 'auto';
            copy.style.width = textArea.offsetWidth + 'px';
            copy.style.height = textArea.offsetHeight + 'px';
            copy.style.position = 'absolute';
            copy.style.left = textArea.offsetLeft + 'px';
            copy.style.top = textArea.offsetTop + 'px';
            document.body.appendChild(copy);
            return copy;
        };
        const getCaretPosition = (textArea: HTMLTextAreaElement) =>
        {
            const start = textArea.selectionStart;
            const end = textArea.selectionEnd;
            const copy = createCopy(textArea);
            const range = document.createRange();

            range.setStart(copy.firstChild as Node, start);
            range.setEnd(copy.firstChild as Node, end);
            const selection = document.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);

            const rangeRect = range.getBoundingClientRect();
            const textAreaRect = textArea.getBoundingClientRect();

            document.body.removeChild(copy);
            textArea.selectionStart = start;
            textArea.selectionEnd = end;
            textArea.focus();
            return {
                left: textAreaRect.left + rangeRect.left,
                top: textAreaRect.top + rangeRect.top
            };
        };
        const handleKeydown = ({ code, ctrlKey, shiftKey }: KeyboardEvent) =>
        {
            updateSelection();
            if (code === "Escape") {
                cancel();
            } else if (code === "Enter" && ctrlKey) {
                save();
            } else if (code === "Space" && shiftKey && inputRef.current)
            {
                setPosition(getCaretPosition(inputRef.current));
            }
        };
        const handleMousedown = () => updateSelection();
        const updateSelection = () =>
        {
            if (inputRef.current && !edited.overrideSelection && (
                inputRef.current.selectionStart !== edited.selectionStart ||
                inputRef.current.selectionEnd !== edited.selectionEnd ||
                inputRef.current?.selectionDirection !== edited.selectionDirection
            ))
            {
                setEdited({
                    script: edited.script,
                    selectionStart: inputRef.current.selectionStart,
                    selectionEnd: inputRef.current.selectionEnd,
                    selectionDirection: inputRef.current.selectionDirection,
                    overrideSelection: false
                })
            }
        };

        window.addEventListener('keydown', handleKeydown);
        window.addEventListener('mousedown', handleMousedown);
        return () =>
        {
            window.removeEventListener('keydown', handleKeydown);
            window.removeEventListener('mousedown', handleMousedown);
        };
    }, [cancel, save, edited, index, onEdit, updateScript, state]);
    return (
        <Box className={classes.state}>
            <InsertMenu onClose={handleMenuClose} onInsert={handleInsert} position={position} />
            <TextField
                classes={{ root: classes.textField }} value={edited.script} inputRef={inputRef} multiline fullWidth
                onBlur={handleBlur} onChange={handleScriptChange} onMouseUp={handleMouseUp} />
            <Box className={classes.tools} display="flex" justifyContent="right">
                <Box className={classes.textTools}>
                    <ScriptVersion project={project} state={state} onSelectVersion={handleSelectVersion}/>
                </Box>
                <StateTools
                    state={state} textClassName={classes.stateKey} linkUrl={stateURL}
                    gotoAction={() => { updateFocus(state); updateMode("graph"); }}/>
                <IconButton className={classes.textTools} size="small" onClick={handleSpellcheckClick}>
                    <Spellcheck/>
                </IconButton>
                <Button className={classes.textTools} onClick={handleCancelClick} title="close without saving (Escape)">
                    Cancel
                </Button>
                <Button className={classes.textTools} onClick={handleSaveClick} title="close and update (Ctrl+Enter)">
                    Save
                </Button>
            </Box>
        </Box>
    );
}

const mapStateToProps = (store: never) => ({
    userKey: selectUserId(store)
});

export default connect(mapStateToProps, {
    updateMode,
    updateFocus
})(EditState);
